import React, { useState } from 'react';
import Tag from '@app/ui/sports-components/Tag';
import { removePostFromCampaign } from './api/postCampaigns';
import connect from '../utils/connect';
import combineActions from '../utils/combineActions';
import sendNotification from '../actions/notifications/send';
import * as Sentry from '@sentry/browser';
import Tooltip from '@app/ui/sports-components/Tooltip';

type ExpandableCampaignTagsProps = {
    client: { id: number };
    postId: number;
    campaigns: { id: number; name: string }[];
    onRemoveCampaign: any;
    sendNotification: (
        message: string,
        config?: { context: 'success' | 'warning' | 'danger' },
    ) => void;
};

const MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE = 2;

const ExpandableCampaignTags = ({
    client,
    postId,
    campaigns = [],
    onRemoveCampaign,
    sendNotification,
}: ExpandableCampaignTagsProps) => {
    const [shouldCollapseCampaigns, setShouldCollapseCampaigns] =
        useState(true);

    const shouldDisplayCollapsedCampaigns =
        shouldCollapseCampaigns &&
        campaigns.length > MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE + 1;

    const _handleRemovePostFromCampaign = (postId, campaign) => async () => {
        try {
            await removePostFromCampaign(client.id, postId, campaign.id);
            onRemoveCampaign(campaign.id, campaign.name);
            sendNotification(
                `Post removed from "${campaign.name}" campaign successfully`,
                {
                    context: 'success',
                },
            );
        } catch (e) {
            sendNotification(e.message, { context: 'warning' });
            Sentry.captureException(e);
        }
    };
    const getDisplayName = campaign => {
        return campaign.name.length > 7
            ? campaign.name.slice(0, 7).trim() + '..'
            : campaign.name;
    };

    if (shouldDisplayCollapsedCampaigns) {
        const expandButtonText = `+ ${campaigns.length - 2} More`;
        return (
            <>
                {campaigns
                    .slice(0, MAX_CAMPAIGN_TAGS_SHOWN_IN_COLLAPSE)
                    .map(campaign => (
                        <div key={campaign.id}>
                            <Tooltip text={campaign.name} position="left">
                                <Tag
                                    size="xs"
                                    text={getDisplayName(campaign)}
                                    hasOption
                                    context="secondary"
                                    onOptionClick={_handleRemovePostFromCampaign(
                                        postId,
                                        campaign,
                                    )}
                                />
                            </Tooltip>
                        </div>
                    ))}
                <span>
                    <Tag
                        onClick={() => setShouldCollapseCampaigns(false)}
                        size="xs"
                        text={expandButtonText}
                        context="secondary"
                    />
                </span>
            </>
        );
    }

    if (!shouldDisplayCollapsedCampaigns) {
        return (
            <>
                {campaigns.map(campaign => (
                    <div key={campaign.id}>
                        <Tooltip text={campaign.name} position="left">
                            <Tag
                                size="xs"
                                text={getDisplayName(campaign)}
                                hasOption
                                context="secondary"
                                onOptionClick={_handleRemovePostFromCampaign(
                                    postId,
                                    campaign,
                                )}
                            />
                        </Tooltip>
                    </div>
                ))}
            </>
        );
    }
};

export default connect(
    ['client'],
    combineActions({ sendNotification }),
)(ExpandableCampaignTags);
